export const findGhostPath = (
  ghostPosition,
  playerPosition,
  isOnPath,
  isSpawnZone,
  shouldFollowPlayer = false,
  maxPathLength = 20,
  spawnExit
) => {
  const ghostInitiallyInSpawn = isSpawnZone(ghostPosition.x, ghostPosition.y);

  return shouldFollowPlayer
    ? findPathToPlayer(
      ghostPosition,
      playerPosition,
      isOnPath,
      isSpawnZone,
      ghostInitiallyInSpawn,
      maxPathLength,
      spawnExit
    )
    : findRandomPath(
      ghostPosition,
      isOnPath,
      isSpawnZone,
      ghostInitiallyInSpawn,
      maxPathLength,
      spawnExit
    );
};

// Find the shortest path to the player
const findPathToPlayer = (
  ghostPosition,
  playerPosition,
  isOnPath,
  isSpawnZone,
  ghostInitiallyInSpawn,
  maxPathLength,
  spawnExit
) => {
  // If the ghost is still in the spawn zone, force it out first
  if (isSpawnZone(ghostPosition.x, ghostPosition.y)) {
    return findRandomPath(
      ghostPosition,
      isOnPath,
      isSpawnZone,
      ghostInitiallyInSpawn,
      maxPathLength,
      spawnExit
    );
  }

  let visited = new Set();
  let queue = [
    {
      x: ghostPosition.x,
      y: ghostPosition.y,
      path: [{ x: ghostPosition.x, y: ghostPosition.y }],
    },
  ];

  let targetX = playerPosition.x;
  let targetY = playerPosition.y;

  while (queue.length > 0) {
    let { x, y, path } = queue.shift();

    // Stop if we reach the playerPosition
    if (x === targetX && y === targetY)
      return extendStraightPath(path, isOnPath, maxPathLength);

    if (path.length >= maxPathLength) continue;

    let sortedDirections = getSortedDirections(x, y, targetX, targetY);

    for (let { dx, dy } of sortedDirections) {
      let newX = x + dx;
      let newY = y + dy;
      let key = `${newX},${newY}`;

      // Ensure the ghost moves only on paths and avoids re-entering spawn
      if (
        isOnPath(newX, newY) &&
        !visited.has(key) &&
        !isSpawnZone(newX, newY)
      ) {
        visited.add(key);
        queue.push({ x: newX, y: newY, path: [...path, { x: newX, y: newY }] });
      }
    }
  }

  return [];
};

// Create a random wandering path (with optimized spawn exit)
const findRandomPath = (
  ghostPosition,
  isOnPath,
  isSpawnZone,
  ghostInitiallyInSpawn,
  maxPathLength,
  spawnExit
) => {
  let path = [{ x: ghostPosition.x, y: ghostPosition.y }];
  let lastDirection = null;
  let stepsInCurrentDirection = 0;
  const maxStraightSteps = 5; // Encourage longer straight movements

  while (path.length < maxPathLength) {
    let possibleMoves = getValidDirections(
      path[path.length - 1],
      isOnPath,
      isSpawnZone,
      ghostInitiallyInSpawn
    );

    if (possibleMoves.length === 0) break;

    let newDirection = null;
    const currentX = path[path.length - 1].x;
    const currentY = path[path.length - 1].y;

    // If the ghost is in the spawn zone, force it toward the exit
    if (isSpawnZone(currentX, currentY)) {
      let exitDirection = null;

      if (currentY < spawnExit.y) {
        exitDirection = { dx: 0, dy: 1 }; // move down
      } else if (currentY > spawnExit.y) {
        exitDirection = { dx: 0, dy: -1 }; // move up
      } else {
        if (currentX < spawnExit.x) {
          exitDirection = { dx: 1, dy: 0 }; // move right
        } else if (currentX > spawnExit.x) {
          exitDirection = { dx: -1, dy: 0 }; // move left
        }
      }

      // Only apply the direction if it's a valid move
      if (exitDirection) {
        newDirection = exitDirection;
      } else {
        // fallback: choose any valid move
        newDirection = possibleMoves[0];
      }
    } else {
      // Standard random wandering logic
      const oppositeDirection = lastDirection
        ? { dx: -lastDirection.dx, dy: -lastDirection.dy }
        : null;

      const filteredMoves = possibleMoves.filter(
        (d) =>
          !oppositeDirection ||
          d.dx !== oppositeDirection.dx ||
          d.dy !== oppositeDirection.dy
      );

      if (
        lastDirection &&
        stepsInCurrentDirection < maxStraightSteps &&
        possibleMoves.some(
          (d) => d.dx === lastDirection.dx && d.dy === lastDirection.dy
        )
      ) {
        newDirection = lastDirection;
        stepsInCurrentDirection++;
      } else {
        newDirection =
          filteredMoves.length > 0
            ? filteredMoves[Math.floor(Math.random() * filteredMoves.length)]
            : possibleMoves[0];
        stepsInCurrentDirection = 0;
      }
    }

    lastDirection = newDirection;
    path.push({
      x: path[path.length - 1].x + newDirection.dx,
      y: path[path.length - 1].y + newDirection.dy,
    });
  }

  return path;
};

// Extend path in a straight line after reaching the playerPosition
const extendStraightPath = (path, isOnPath, maxPathLength) => {
  let lastMove = {
    dx: path[path.length - 1].x - path[path.length - 2]?.x || 0,
    dy: path[path.length - 1].y - path[path.length - 2]?.y || 0,
  };

  while (path.length < maxPathLength) {
    let newX = path[path.length - 1].x + lastMove.dx;
    let newY = path[path.length - 1].y + lastMove.dy;
    if (!isOnPath(newX, newY)) break;
    path.push({ x: newX, y: newY });
  }

  return path;
};

// Sort directions based on closeness to the target
const getSortedDirections = (x, y, targetX, targetY) => {
  const directions = [
    { dx: 0, dy: -1 },
    { dx: 0, dy: 1 },
    { dx: -1, dy: 0 },
    { dx: 1, dy: 0 },
  ];

  return directions.sort(
    (a, b) =>
      Math.abs(x + a.dx - targetX) +
      Math.abs(y + a.dy - targetY) -
      (Math.abs(x + b.dx - targetX) + Math.abs(y + b.dy - targetY))
  );
};

// Get valid directions for random movement
const getValidDirections = (
  position,
  isOnPath,
  isSpawnZone,
  ghostInitiallyInSpawn
) => {
  const directions = [
    { dx: 0, dy: -1 },
    { dx: 0, dy: 1 },
    { dx: -1, dy: 0 },
    { dx: 1, dy: 0 },
  ];

  return directions.filter(({ dx, dy }) => {
    let newX = position.x + dx;
    let newY = position.y + dy;
    const shouldAvoidSpawn = ghostInitiallyInSpawn
      ? false
      : isSpawnZone(newX, newY);
    return isOnPath(newX, newY) && !shouldAvoidSpawn;
  });
};
